/**
 * Offer Availability Schemas
 *
 * DTOs for:
 * - GET /offers/:offerId/availability  — availability matrix response
 * - PUT /offers/:offerId/units/:unitId/dates  — atomic replace of unit date ranges
 *
 * Traceability: REQ-7.3 -> offer-draft-availability / Replace Unit Date Ranges
 */
import { z } from 'zod';
import { OfferIdSchema, UserIdSchema } from './offer.js';
import { OfferUnitIdSchema } from './offer-unit.js';
import { ReservationStatusSchema } from './reservation-status.js';
import { PERIOD_GRANULARITY_VALUES, type PeriodGranularity } from '../period-granularity.js';
import { AvailabilityPeriodCellSchema } from './availability.js';

// ─── PeriodGranularity ────────────────────────────────────────────────────────

export const PeriodGranularitySchema = z.enum(PERIOD_GRANULARITY_VALUES);

// ─── DateRangeSchema ──────────────────────────────────────────────────────────
// Represents a single [from, to] date range (ISO date strings).
// Enforces from <= to via refinement.

export const DateRangeSchema = z
  .object({
    from: z.string().date(),
    to: z.string().date(),
  })
  .refine((val) => val.from <= val.to, {
    message: 'from date must be less than or equal to to date',
    path: ['from'],
  });

export type DateRange = z.infer<typeof DateRangeSchema>;

// ─── Overlap detection helper ─────────────────────────────────────────────────
// Generic over the bound type (ISO date string or Date) so this single
// implementation covers both string-based DateRange callers (this schema's own
// overlap refinement, web availability grid) and Date-based callers (packages/db
// reservation overlap checks against `date` columns, which drizzle returns as
// Date). `<=` is valid on both `string` and `Date` in TS strict mode.

export function rangesOverlap<T extends string | Date>(
  a: { from: T; to: T },
  b: { from: T; to: T },
): boolean {
  // Two date ranges overlap when: a.from <= b.to AND b.from <= a.to
  return a.from <= b.to && b.from <= a.to;
}

// ─── ReplaceUnitDatesRequestSchema ───────────────────────────────────────────
// PUT body for atomic replace of a unit's date selections.
// - Max 24 ranges per unit (per design D-2).
// - All ranges must be non-overlapping.
// - updatedAt: ISO timestamp of the parent offer for optimistic-lock check.

export const ReplaceUnitDatesRequestSchema = z
  .object({
    ranges: z.array(DateRangeSchema).max(24),
    updatedAt: z.coerce.date(),
  })
  .superRefine((val, ctx) => {
    const sorted = [...val.ranges].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i]!;
      const next = sorted[i + 1]!;
      if (rangesOverlap(current, next)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Ranges overlap: [${current.from}..${current.to}] and [${next.from}..${next.to}]`,
          path: ['ranges'],
        });
        return;
      }
    }
  });

export type ReplaceUnitDatesRequest = z.infer<typeof ReplaceUnitDatesRequestSchema>;

// ─── CellStatus (legacy taxonomy) ────────────────────────────────────────────
// Kept for backward compatibility with the existing API service and web consumers.
// TODO(PR2b/PR2c): replace with CellState from ./availability.js when API (task 2.1)
// and web (task 4.2) consumers are retrofitted.

export const CellStatusSchema = z.enum([
  'FREE',
  'UNKNOWN',
  'POTENTIAL',
  'OPTION-MINE',
  'OPTION-OTHER',
  'RESERVED-MINE',
  'RESERVED-OTHER',
  'SOLD',
]);
export type CellStatus = z.infer<typeof CellStatusSchema>;

// ─── AvailabilityCellSchema ──────────────────────────────────────────────────
// A single (unit, period) cell in the availability matrix.
// Conflict fields (conflictingOfferId, title, ownerSummary) only present when
// the caller has sufficient access.

export const ConflictingOfferOwnerSchema = z.object({
  id: UserIdSchema,
  displayName: z.string(),
});
export type ConflictingOfferOwner = z.infer<typeof ConflictingOfferOwnerSchema>;

export const AvailabilityCellSchema = z.object({
  start: z.string().date(),
  end: z.string().date(),
  status: CellStatusSchema,
  reservationId: z.number().int().positive().nullable().optional(),
  reservationStatus: ReservationStatusSchema.nullable().optional(),
  conflictingOfferId: OfferIdSchema.nullable().optional(),
  title: z.string().nullable().optional(),
  ownerSummary: ConflictingOfferOwnerSchema.nullable().optional(),
  ttlExpiresAt: z.coerce.date().nullable().optional(),
});
export type AvailabilityCell = z.infer<typeof AvailabilityCellSchema>;

// ─── UnitAvailabilityRowSchema ───────────────────────────────────────────────
// One row in the availability matrix: the unit's metadata + its period cells.
// NOTE: periods now uses AvailabilityPeriodCellSchema (entries[] model) from
// the availability-calendar reshape (task 2.1). Each period carries entries:CellEntry[].

export const UnitAvailabilityRowSchema = z.object({
  unitId: OfferUnitIdSchema,
  mediaUnitId: z.number().int().positive(),
  unitLabel: z.string(),
  /** Raw supplier number, shown as a secondary identifier alongside unitLabel
   *  (which now prioritizes grwId — the catalog-consistent primary number). */
  supplierNumber: z.string().nullable(),
  city: z.string().nullable(),
  granularity: PeriodGranularitySchema,
  selectedRanges: z.array(DateRangeSchema),
  periods: z.array(AvailabilityPeriodCellSchema),
});
export type UnitAvailabilityRow = z.infer<typeof UnitAvailabilityRowSchema>;

// ─── OfferAvailabilityListQuerySchema ───────────────────────────────────────
// OPTIONAL cursor-based pagination query params for GET /offers/:offerId/availability.
// No sortBy/sortDir/filters (no filtering on this endpoint). Unlike the
// units/pricing list query schemas, `pageSize` has NO `.default()` here —
// "absent" must mean "return everything" for backward compatibility with
// getPublicLinkAvailability() and other internal callers that never pass
// pagination params (see the Availability Lookup Endpoint requirement).
//
// `unitIds`/`from`/`to` (task 1.2, CU-86cbc3r8b): let the merged offer table
// ask for availability matching only the rows it has filtered/sorted/paged to
// and only the period window it renders, instead of transferring a long
// campaign's whole period history. Combinable with `cursor`/`pageSize` — an
// orthogonal, independent set of params (Availability Lookup Endpoint
// requirement's "Naming units SHALL be combinable with the existing
// pagination"). None of the three has a `.default()`, matching `pageSize`
// above: omitting all of them must stay byte-for-byte identical to today's
// whole-offer, unpaginated response. Query-level narrowing (offer-membership
// intersection, range clipping, and the granularity-aware "at most 400
// periods" bound) is owned by the `[api]` boundary (task 2.2) — this schema
// only accepts the shape and rejects an oversized id list or an inverted
// range. `unitIds` uses a plain coerced-number array (not a branded ID
// schema), mirroring `reservation-list.ts`'s `holderUserId`/`offerId` GET
// query filters.

export const OfferAvailabilityListQuerySchema = z
  .object({
    /** Offset-based pagination cursor. Pass meta.nextCursor to fetch the next page. */
    cursor: z
      .string()
      .regex(/^\d+$/, 'cursor must be a non-negative integer string')
      .max(20)
      .optional(),
    pageSize: z.coerce.number().min(1).max(200).optional(),
    /**
     * Optional list of unit ids to report on. Intersected with the offer's
     * own membership server-side (task 2.2) — a unit outside the offer is
     * silently dropped, never returned. Capped at 200 — the same maximum
     * page size the offer listings already accept, so one request always
     * covers at most one page of rows.
     */
    unitIds: z.array(z.coerce.number().int().positive()).max(200).optional(),
    /** Period range start (YYYY-MM-DD). Applied only when `to` is also present. */
    from: z.string().date().optional(),
    /** Period range end (YYYY-MM-DD). Applied only when `from` is also present. */
    to: z.string().date().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.from !== undefined && value.to !== undefined && value.to < value.from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'to must not be earlier than from',
        path: ['to'],
      });
    }
  });
export type OfferAvailabilityListQuery = z.infer<typeof OfferAvailabilityListQuerySchema>;

// ─── OfferAvailabilityResponseSchema ────────────────────────────────────────
// Response from GET /offers/:offerId/availability.
// `meta` is OPTIONAL and present only when the caller supplied pagination
// params — omitted entirely (not present, not null) on the unpaginated path
// so the shape is byte-for-byte identical to the pre-pagination contract.

export const OfferAvailabilityResponseSchema = z.object({
  defaultDateFrom: z.string().date().nullable(),
  defaultDateTo: z.string().date().nullable(),
  defaultPeriodGranularity: PeriodGranularitySchema.nullable(),
  units: z.array(UnitAvailabilityRowSchema),
  meta: z
    .object({
      nextCursor: z.string().nullable(),
      pageSize: z.number(),
      totalCount: z.number(),
    })
    .optional(),
});
export type OfferAvailabilityResponse = z.infer<typeof OfferAvailabilityResponseSchema>;

// Re-export the PeriodGranularity type from its canonical location for consumers
export type { PeriodGranularity };
